import dayjs from 'dayjs';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Legend, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ContentPlaceholder } from '../../../../../../components/content-placeholder/content-placeholder';
import { useStrategyStore, useThroughputTabStore } from '../../../../../../store/strategy-store/strategy-store';
import {
  setPerformanceActivePanel,
  setThroughputSelectedDate,
  setThroughputSelectedOutcome,
} from '../../../../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../../../../store/strategy-store/strategy-store.type';
import { skyLight, skyLightest } from '../../../../../../styles/design-tokens';
import { getMetricLabel } from '../../../../strategy.helpers';
import { CHART_COLORS } from '../../../initiative-performance.constants';
import { ContributingOutcomesChartLabels } from './contributing-outcomes-chart-labels';
import { ContributingOutcomesChartLegend } from './contributing-outcomes-chart-legend';
import { ContributingOutcomesChartTooltip } from './contributing-outcomes-chart-tooltip';
import { useChartData } from './contributing-outcomes-chart.hooks';

export const ContributingOutcomesChart = forwardRef<HTMLDivElement>((_, ref) => {
  const { t } = useTranslation();
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const selectedOutcomes = useThroughputTabStore((state) => state.selectedOutcomes);
  const metric = useThroughputTabStore((state) => state.metric);

  const barColors = Object.fromEntries(
    selectedOutcomes.map((outcome, index) => {
      return [outcome.id, CHART_COLORS[index]];
    }),
  );

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const chartData = useChartData();
  const hasData = Array.isArray(chartData) && chartData.length > 0;

  if (!initiative || !hasData) {
    return <ContentPlaceholder />;
  }

  if (selectedOutcomes.length === 0) {
    return <ContentPlaceholder noFiltersApplied />;
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <BarChart data={chartData} margin={{ top: 20 }} barGap={2} barCategoryGap="10%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={skyLight} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(value) => dayjs(value).format('MMM, YYYY')}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: `${getMetricLabel(metric, t)} ${t('strategy.initiative_performance.chart_labels.per_month')}`,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
            }}
          />
          <Tooltip
            content={<ContributingOutcomesChartTooltip barColors={barColors} outcomes={selectedOutcomes} />}
            cursor={{ fill: skyLightest }}
          />
          <Legend
            content={
              <ContributingOutcomesChartLegend
                outcomes={selectedOutcomes}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
                barColors={barColors}
              />
            }
            verticalAlign="bottom"
            height={36}
          />
          {selectedOutcomes.map((outcome) => {
            const color = barColors[outcome.id];

            return (
              <Bar
                key={outcome.key}
                dataKey={outcome.id}
                label={
                  hoveredItem === null || hoveredItem === outcome.id ? <ContributingOutcomesChartLabels /> : undefined
                }
                width={10}
                animationDuration={0}
                background={{ fill: color, opacity: 0.1 }}
                onClick={(chartData) => {
                  setThroughputSelectedOutcome(outcome);
                  setThroughputSelectedDate(dayjs((chartData as any).date, 'YYYY-MM').toDate());
                  setPerformanceActivePanel(Panel.Side);
                }}
                shape={
                  <Rectangle
                    fill={color}
                    onMouseEnter={() => setHoveredItem(outcome.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    opacity={hoveredItem === null || hoveredItem === outcome.id ? 0.8 : 0.2}
                    style={{
                      cursor: hoveredItem === outcome.id ? 'pointer' : 'default',
                    }}
                  />
                }
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});
